/**
 * Signup attribution capture — where did this browser come from?
 *
 * Every visitor gets a small, durable stash describing their acquisition:
 *
 *   - the CAMPAIGN block: whatever marketing params the tagged URL carried
 *     (`utm_*`, `source`, `fbclid`, `gclid`, campaign/ad ids+names, and the
 *     per-recipient cold-email `ref` token — see _docs/coldemail-attribution.md).
 *     Absent entirely for organic arrivals.
 *   - the TOUCH block: `landing_path` (pathname), `referrer` (referring HOST,
 *     cross-origin only), `first_seen_at` (when this browser first landed — set
 *     once, never overwritten) and `touched_at` (when the rest was last written).
 *
 * Semantics: last tagged touch wins. The first visit — tagged or not — writes
 * the stash. A later TAGGED arrival REPLACES the campaign + landing + referrer
 * wholesale (no merge: a June `fbclid` must not survive next to a September
 * `utm_source=cold_email`) and keeps `first_seen_at`. A later UNTAGGED arrival
 * never overwrites anything. So a teacher who browsed organically in June and
 * converts via a tagged cold email in September is filed as cold_email — the
 * answer the attribution question actually needs.
 *
 * Storage: localStorage (durable across tabs/sessions — it has to survive
 * "read the email on my phone, sign up on the laptop tab I open tomorrow"),
 * with a 180-day first-party cookie when localStorage is blocked and a
 * module-level copy as the last resort for a single page load (this is an SPA:
 * landing → client-side nav → OTP verify all happen without a reload).
 *
 * Consumers: `buildPhoneVerifyBody` ships the stash with /auth/phone-verify
 * (the API writes it to `leads_data` for every NEW signup); `registerAttribution`
 * mirrors it onto every PostHog event as `ad_*` super props; `identifyUser`
 * freezes it onto the person as `$set_once` (see `personAttribution` for the
 * "is this touch actually older than the account?" gate).
 */
import { getEntrySource } from "./entry-source.js";
import { getMetaBrowserIds, newMetaEventId } from "./meta-pixel.js";
import { getExposure, type TlExposure } from "./tl-experiment.js";

const STORAGE_KEY = "sa:attribution";
// Cookie fallback for when localStorage is blocked (private mode / quota) —
// without it those visitors would be misfiled as organic at signup. `:` is
// not cookie-name-safe, hence the underscore variant. Long-lived to match
// localStorage's "survives the tab" lifetime as closely as a cookie can.
const COOKIE_KEY = "sa_attribution";
const COOKIE_MAX_AGE_S = 180 * 24 * 60 * 60;
// Browsers cap a cookie at ~4 KB and DROP it silently when exceeded — keep a
// margin for the name, attributes and percent-encoding overhead.
const COOKIE_BUDGET_CHARS = 3800;
// Per-session marker of the campaign touch we already emitted `ad_visit` for,
// so a same-tab reload of a tagged URL doesn't double-count the visit while a
// re-click of the SAME link next week (deterministic `ref`, same utm_*) does.
const VISIT_MARKER_KEY = "sa:attribution:visit";
// Client-side cap per value (the API caps again at 512). Keeps the cookie
// fallback inside its budget and refuses pathological query strings.
const MAX_VALUE_LEN = 256;

// Marketing params we recognize. Presence of any ⇒ a campaign-sourced visit
// (paid ads AND outbound campaigns — e.g. cold email links carry
// `utm_source=cold_email` + a per-recipient `ref` token, see
// _docs/coldemail-attribution.md). Order matters only for the visit signature.
// Keep in lockstep with LEAD_CAMPAIGN_KEYS in packages/db/src/schema.ts — the
// server derives "is this signup campaign-sourced?" from that list, so a key
// captured here but missing there would file a tagged signup as organic.
export const CAMPAIGN_KEYS = [
  "source",
  "fbclid",
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "campaign_id",
  "campaign_name",
  "adset_id",
  "adset_name",
  "ad_id",
  "ad_name",
  // Per-recipient token on cold-email links: ties a signup back to the exact
  // emailed address (deterministic HMAC of the email — scripts/coldemail-links.ts).
  "ref",
] as const;

/** The touch block — present on every stash written since 2026-08-30. */
export const TOUCH_KEYS = ["landing_path", "referrer", "first_seen_at", "touched_at"] as const;

/** Every key the stash can carry. `registerAttribution` uses this to
 *  UNREGISTER stale `ad_*` super props a later touch no longer carries. */
export const ALL_ATTRIBUTION_KEYS: readonly string[] = [...CAMPAIGN_KEYS, ...TOUCH_KEYS];

export type Attribution = Record<string, string>;

/** Last-resort copy for a single page load when every storage layer is blocked. */
let memStash: string | null = null;

function readCookie(): string | null {
  try {
    for (const part of document.cookie.split(";")) {
      const eq = part.indexOf("=");
      if (eq === -1) continue;
      if (part.slice(0, eq).trim() === COOKIE_KEY) {
        const v = part.slice(eq + 1).trim();
        return v ? decodeURIComponent(v) : null;
      }
    }
  } catch {
    // document.cookie unavailable — nothing stashed there.
  }
  return null;
}

/**
 * Read the raw stash. Order: localStorage (durable) → in-memory copy →
 * legacy sessionStorage (pre-2026-08-30 builds; an in-flight tab at deploy
 * time still has one) → cookie (new long-lived fallback, or the legacy session
 * cookie — same name).
 */
function readStash(): string | null {
  let blocked = false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return raw;
  } catch {
    blocked = true;
  }
  // The in-memory copy stands in ONLY when localStorage is unreadable — a
  // readable-but-empty localStorage means the stash was genuinely cleared.
  if (blocked && memStash) return memStash;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return raw;
  } catch {
    // sessionStorage blocked too.
  }
  return readCookie();
}

// What to give up first when the cookie fallback is over budget: descriptive
// / duplicate Meta fields before ids, ids before the UTM triple + `ref`. What
// is never dropped (utm_source / utm_medium / utm_campaign / ref / source /
// landing_path / first_seen_at / touched_at) gets its VALUES shortened instead
// — a truncated campaign name still attributes; a dropped cookie doesn't.
const COOKIE_DROP_ORDER = [
  "utm_content",
  "utm_term",
  "adset_name",
  "ad_name",
  "campaign_name",
  "adset_id",
  "gclid",
  "fbclid",
  "referrer",
  "ad_id",
  "campaign_id",
] as const;
const COOKIE_VALUE_CAPS = [128, 64, 32] as const;

/**
 * JSON for the cookie fallback, guaranteed to fit its budget: drop keys in
 * COOKIE_DROP_ORDER until it fits, then shorten the remaining values. A
 * small stash comes back untouched. Pure.
 */
export function serializeForCookie(a: Attribution): string {
  const copy: Attribution = { ...a };
  let raw = JSON.stringify(copy);
  const fits = () => encodeURIComponent(raw).length <= COOKIE_BUDGET_CHARS;
  for (const drop of COOKIE_DROP_ORDER) {
    if (fits()) return raw;
    if (!(drop in copy)) continue;
    delete copy[drop];
    raw = JSON.stringify(copy);
  }
  for (const cap of COOKIE_VALUE_CAPS) {
    if (fits()) return raw;
    for (const k of Object.keys(copy)) copy[k] = copy[k]!.slice(0, cap);
    raw = JSON.stringify(copy);
  }
  return raw;
}

/**
 * Persist the stash: localStorage first (and retire any legacy sessionStorage
 * copy so the read order can't resurrect it), else a long-lived cookie. The
 * in-memory copy is always kept so the current page load can read it back even
 * when both layers are blocked. Silent when everything is unavailable — the
 * cookie setter never throws; strict cookie blocking just ignores it. `Secure`
 * rides along on https (production); omitted on plain-http origins (local dev,
 * DOM test environments) where browsers would silently drop a Secure cookie.
 */
function writeStash(a: Attribution): void {
  const raw = JSON.stringify(a);
  memStash = raw;
  try {
    localStorage.setItem(STORAGE_KEY, raw);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to retire.
    }
    return;
  } catch {
    // localStorage blocked — fall back to the cookie.
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(serializeForCookie(a))}; path=/; Max-Age=${COOKIE_MAX_AGE_S}; SameSite=Lax${secure}`;
}

/** Pick + trim + cap the recognised marketing params from a query string. Pure. */
export function pickCampaignParams(search: string): Attribution {
  const params = new URLSearchParams(search);
  const out: Attribution = {};
  for (const key of CAMPAIGN_KEYS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim().slice(0, MAX_VALUE_LEN);
  }
  return out;
}

/**
 * Referring HOST, or null for empty / unparsable / same-origin referrers.
 * Host rather than URL: cross-origin browsers send origin-only by default
 * anyway, and a hostname keeps the PostHog `ad_referrer` breakdown usable.
 * Same-origin is dropped because a full reload mid-site (e.g. the OTP page)
 * would otherwise "refer" us to ourselves. Pure.
 */
export function referrerHost(
  referrer: string,
  ownHost: string = window.location.host,
): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).host;
    if (!host || host === ownHost) return null;
    return host.slice(0, MAX_VALUE_LEN);
  } catch {
    return null;
  }
}

function campaignSignature(campaign: Attribution): string {
  return JSON.stringify(campaign);
}

/** Emit-once-per-session guard for `ad_visit`. Returns true when this campaign
 *  touch has NOT been counted in this session yet (and marks it). Storage
 *  blocked ⇒ true (at worst a duplicate on reload in strict private mode). */
function markVisit(campaign: Attribution): boolean {
  const sig = campaignSignature(campaign);
  try {
    if (sessionStorage.getItem(VISIT_MARKER_KEY) === sig) return false;
    sessionStorage.setItem(VISIT_MARKER_KEY, sig);
  } catch {
    // No session marker available — count it.
  }
  return true;
}

/**
 * Capture this page load's acquisition signal and reconcile it with the stash.
 * Runs synchronously at boot (main.tsx) — before React Router can drop the
 * query string. Rules (see the module doc): first visit writes everything; a
 * later tagged arrival replaces campaign + landing + referrer and keeps
 * `first_seen_at`; a later untagged arrival changes nothing (except promoting
 * a legacy session-scoped stash into durable storage).
 *
 * Returns the fresh campaign touch — campaign params + `landing_path` +
 * `referrer`, no timestamps — the first time this session sees it (so the
 * caller can emit ONE `ad_visit`), or `null` for organic loads and same-session
 * repeats of a touch already counted.
 */
export function captureAttribution(
  search: string = window.location.search,
  pathname: string = window.location.pathname,
  referrer: string = typeof document === "undefined" ? "" : document.referrer,
  now: Date = new Date(),
): Attribution | null {
  try {
    const campaign = pickCampaignParams(search);
    const tagged = Object.keys(campaign).length > 0;
    const prev = getAttribution();
    const nowIso = now.toISOString();
    const host = referrerHost(referrer);
    const touch: Attribution = { landing_path: pathname, ...(host ? { referrer: host } : {}) };

    if (prev && !tagged) {
      // Untagged repeat visit: never overwrite. The one exception is a legacy
      // (pre-durable) stash, which lacks the touch timestamps — promote it so
      // it stops dying with the tab.
      if (!prev.first_seen_at) {
        writeStash({ ...prev, first_seen_at: nowIso, touched_at: prev.touched_at ?? nowIso });
      }
      return null;
    }

    if (prev && tagged && !markVisit(campaign)) {
      // Same-session reload of a tagged URL already reflected in the stash —
      // don't bump touched_at, don't double-count the visit.
      return null;
    }

    const stash: Attribution = {
      ...campaign,
      ...touch,
      first_seen_at: prev?.first_seen_at ?? nowIso,
      touched_at: nowIso,
    };
    writeStash(stash);

    if (!tagged) return null; // organic first visit — stashed, but not an ad visit
    if (!prev && !markVisit(campaign)) return null; // marker survived a cleared stash
    return { ...campaign, ...touch };
  } catch {
    // Every storage layer blocked — degrade silently.
    return null;
  }
}

/**
 * Flatten captured attribution into PostHog-friendly, `ad_`-prefixed scalar
 * properties (e.g. `source` → `ad_source`, `ad_id` → `ad_ad_id`). The prefix
 * namespaces them away from PostHog's own URL-parsed `utm_*`/`fbclid` autocapture
 * so we can register them as durable super/person properties and slice every
 * downstream event (incl. signup/placement, long after the query string is
 * gone) by ad source/campaign. Empty/whitespace values are dropped; `touched_at`
 * is stash bookkeeping (the identify gate), not a segment, so it stays out. Pure.
 */
export function attributionProps(a: Attribution | null): Record<string, string> {
  if (!a) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(a)) {
    if (k === "touched_at") continue;
    if (typeof v === "string" && v.trim()) out[`ad_${k}`] = v.trim();
  }
  return out;
}

// How much older than the touch an account may be and still take the touch as
// its acquisition. Covers client-clock skew (touched_at is browser time,
// created_at is server time) without letting a months-old account absorb a
// campaign click it clearly didn't come from.
const PERSON_ATTRIBUTION_SKEW_MS = 24 * 60 * 60 * 1000;

/**
 * The `$set_once` person props for an identified account, or `{}` when the
 * stash describes a touch that happened AFTER the account already existed.
 * Without this gate, every pre-existing organic teacher who clicks a tagged
 * cold email would be frozen in PostHog as cold_email-sourced while the DB
 * (written once, at signup) says otherwise. A legacy stash (no `touched_at`)
 * or an unknown `created_at` behaves as before (props attached). Pure.
 */
export function personAttribution(
  a: Attribution | null,
  accountCreatedAt: string | null | undefined,
): Record<string, string> {
  if (!a) return {};
  if (a.touched_at && accountCreatedAt) {
    const touched = Date.parse(a.touched_at);
    const created = Date.parse(accountCreatedAt);
    if (
      Number.isFinite(touched) &&
      Number.isFinite(created) &&
      created + PERSON_ATTRIBUTION_SKEW_MS < touched
    ) {
      return {};
    }
  }
  return attributionProps(a);
}

/** Read the stashed attribution (localStorage / memory / legacy session
 *  storage / cookie), or null when nothing has been captured. */
export function getAttribution(): Attribution | null {
  try {
    const raw = readStash();
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const out: Attribution = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string") out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

/**
 * Build the `/auth/phone-verify` request body shared by both OTP surfaces
 * (AuthGate popup + /signup page): phone + code, the stashed attribution
 * (campaign params when tagged; landing/referrer/first_seen for everyone —
 * omitted only when every storage layer was blocked), and a `meta` block for
 * the server-side Conversions API Lead — a fresh dedup `event_id` plus Meta's
 * `_fbp`/`_fbc` browser cookies when present, plus the test-landing
 * experiment arm when this browser was exposed to one (TEX-1637 — rides into
 * the CAPI Lead's custom data). Callers must fire the browser Lead pixel with
 * the returned `leadEventId` so Meta counts the pair once.
 */
export function buildPhoneVerifyBody(
  phone: string,
  code: string,
  marketingOptIn?: boolean,
): { body: Record<string, unknown>; leadEventId: string } {
  const attribution = getAttribution();
  const exposure = getExposure();
  const leadEventId = newMetaEventId();
  const body: Record<string, unknown> = {
    phone,
    code,
    meta: {
      event_id: leadEventId,
      ...getMetaBrowserIds(),
      ...(exposure ? { variant: exposure.variant } : {}),
    },
  };
  if (attribution) body.attribution = attribution;
  // First-touch signup entry surface (test landing / quiz / game / organic …).
  // Recorded server-side for EVERY new signup to tailor SMS sequences; the API
  // gates it to first-verification, so sending it on a sign-in is harmless.
  const entrySource = getEntrySource();
  if (entrySource) body.entry_source = entrySource;
  // Persist SMS marketing consent the moment the row is created, so it survives
  // an abandoned profile step. Only the signup popup passes this; sign-in omits it.
  if (marketingOptIn !== undefined) body.marketing_opt_in = marketingOptIn;
  return { body, leadEventId };
}

/**
 * Pure helper: merge base Meta-Pixel Lead-event params with the captured
 * attribution — and, when the visitor was exposed to a test-landing
 * experiment arm, the arm itself (TEX-1637) — so the Lead event carries ad +
 * experiment context. Kept pure (attribution/exposure passed in) so it's
 * unit-testable. Uses distinct keys (`ad_source`) to avoid clobbering the
 * existing surface `source` ("popup"/"signup_page").
 */
export function leadEventParams(
  base: Record<string, unknown>,
  attribution: Attribution | null,
  exposure?: TlExposure | null,
): Record<string, unknown> {
  const extra: Record<string, unknown> = {};
  if (attribution) {
    if (attribution.source) extra.ad_source = attribution.source;
    if (attribution.campaign_id) extra.campaign_id = attribution.campaign_id;
    if (attribution.ad_id) extra.ad_id = attribution.ad_id;
  }
  if (exposure) {
    extra.variant = exposure.variant;
    extra.test_slug = exposure.test_slug;
  }
  return Object.keys(extra).length ? { ...base, ...extra } : base;
}
